import React, { useEffect, useState } from "react";
import { Order } from "../types";
import { orderService } from "../services/dataService";
import "./SendDocumentModal.css";

interface Props {
  order: Order;
  onClose: () => void;
  onSaved: (invoice: string | null, invoiceNumber: string | null) => void;
}

/**
 * Elle fatura bilgisi girişi — YALNIZ fatura modülü lisansta yokken görünür.
 * Kağıt/harici kesilen faturanın numarası ve (isteğe bağlı) seri/açıklaması
 * siparişe işlenir. Modül varken numara belge servisinden gelir; sunucu bu ucu
 * 403 ile kapatır.
 */
const InvoiceNumberModal: React.FC<Props> = ({ order, onClose, onSaved }) => {
  const [invoice, setInvoice] = useState(order.invoice || "");
  const [invoiceNumber, setInvoiceNumber] = useState(order.invoiceNumber || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const save = async () => {
    const no = invoiceNumber.trim();
    if (!no) return setError("Fatura numarası boş olamaz.");
    setSaving(true);
    setError("");
    try {
      await orderService.updateInvoice(order.orderId, {
        invoice: invoice.trim() || null,
        invoiceNumber: no,
      });
      onSaved(invoice.trim() || null, no);
      onClose();
    } catch (err: any) {
      const d = err?.response?.data;
      setError(d?.message || (typeof d === "string" ? d : "Fatura bilgisi kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ic-overlay" onClick={() => !saving && onClose()}>
      <div className="ic-panel ic-panel-sm" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="ic-bar">
          <div className="ic-bar-left">
            <h2>Fatura No Gir</h2>
            <span className="ic-chip neutral">Elle giriş</span>
          </div>
          <button className="ic-close" onClick={onClose} aria-label="Kapat" disabled={saving}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>

        <div className="ic-body">
          <div className="ic-doc" style={{ padding: "1.25rem 1.5rem" }}>
            <div className="ic-party" style={{ marginBottom: "1rem" }}>
              <div className="ic-party-label">Sipariş</div>
              <div className="ic-party-name">{order.firm?.firmName || "—"}</div>
              <div className="ic-party-line">
                {order.model?.modelCode || "—"} · {order.quantity} {typeof order.unit === "string" ? order.unit : "adet"}
              </div>
            </div>

            {error && <div className="ic-alert error">{error}</div>}

            <div className="ic-note">
              <label htmlFor="im-no">Fatura Numarası</label>
              <input
                id="im-no"
                className="ic-in"
                value={invoiceNumber}
                maxLength={50}
                placeholder="Örn. ABC2026000000123"
                autoFocus
                onChange={(e) => { setInvoiceNumber(e.target.value); setError(""); }}
              />
            </div>
            <div className="ic-note" style={{ marginTop: "0.875rem" }}>
              <label htmlFor="im-inv">Seri / Açıklama (isteğe bağlı)</label>
              <input
                id="im-inv"
                className="ic-in"
                value={invoice}
                maxLength={100}
                placeholder="Örn. ABC serisi, kağıt fatura"
                onChange={(e) => setInvoice(e.target.value)}
              />
            </div>
            <div className="ic-total-hint" style={{ marginTop: "0.75rem" }}>
              Bu bilgi sistem dışında kesilmiş faturayı siparişle eşlemek içindir; belge
              üretilmez ve GİB'e gönderim yapılmaz.
            </div>
          </div>
        </div>

        <footer className="ic-actions">
          <span className="ic-actions-summary">{order.invoiceNumber ? `Mevcut: ${order.invoiceNumber}` : "Henüz fatura no girilmemiş"}</span>
          <div className="ic-actions-btns">
            <button className="ic-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button className="ic-btn primary" onClick={save} disabled={saving}>
              {saving ? (<><span className="ic-spin" aria-hidden="true" />Kaydediliyor…</>) : "Kaydet"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default InvoiceNumberModal;
